import axios from 'axios';
import { prodDb, subDb, userDb } from '../../config/pouch';
import { fetchWithOfflineSupport, getAllProductsOffline, getAllSubstancesOffline, getAllProfilesOffline } from '../../helpers/offlineApi';
import { setLoading, setAuthenticating, setMessage } from './status';
import { SYNC_OFFLINE_USER } from './user';

export const SET_LOCAL_PRODUCTS = 'SET_LOCAL_PRODUCTS';
export const SET_LOCAL_SUBSTANCES = 'SET_LOCAL_SUBSTANCES';
export const SET_LOCAL_CATEGORIES = 'SET_LOCAL_CATEGORIES';
export const SET_LOCAL_PROFILES = 'SET_LOCAL_PROFILES';
export const SET_PRODUCT_DOWNLOAD_IDS = 'SET_PRODUCT_DOWNLOAD_IDS';
export const SET_SUBSTANCE_DOWNLOAD_IDS = 'SET_SUBSTANCE_DOWNLOAD_IDS';
export const SET_PROFILE_DOWNLOAD_IDS = 'SET_PROFILE_DOWNLOAD_IDS';
export const SET_OFFLINE_MODE = 'SET_OFFLINE_MODE';

const API_BASE_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';
const API_FIRESTORE_USERS = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/firestore/users';
const CHUNK_SIZE = 100;
const REQUEST_DELAY = 1000;

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const isOnline = () => {
    return navigator.onLine;
};

const testNetworkConnectivity = async (timeoutMs = 5000) => {
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const response = await fetch(API_FIRESTORE_USERS, {
            method: 'GET',
            signal: controller.signal,
            cache: 'no-store',
            mode: 'cors'
        });

        clearTimeout(timeoutId);
        return response.ok;
    } catch (error) {
        return false;
    }
};

const apiCall = async (url, options = {}) => {
    const defaultOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        timeout: 30000,
        ...options
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), defaultOptions.timeout);

    try {
        const response = await fetch(url, {
            ...defaultOptions,
            signal: controller.signal,
            mode: 'cors'
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error('Timeout - Connexion trop lente');
        }
        throw error;
    }
};

export const setLocalProducts = (prods) => ({
    type: SET_LOCAL_PRODUCTS,
    payload: prods
});

export const setLocalSubstances = (subs) => ({
    type: SET_LOCAL_SUBSTANCES,
    payload: subs
});

export const setLocalCategories = (cats) => ({
    type: SET_LOCAL_CATEGORIES,
    payload: cats
});

export const setLocalProfiles = (profiles) => ({
    type: SET_LOCAL_PROFILES,
    payload: profiles
});

export const setProductDownloadIDs = (ids) => ({
    type: SET_PRODUCT_DOWNLOAD_IDS,
    payload: ids
});

export const setSubstanceDownloadIDs = (ids) => ({
    type: SET_SUBSTANCE_DOWNLOAD_IDS,
    payload: ids
});

export const setProfileDownloadIDs = (ids) => ({
    type: SET_PROFILE_DOWNLOAD_IDS,
    payload: ids
});

export const setOfflineMode = (isOffline) => ({
    type: SET_OFFLINE_MODE,
    payload: isOffline
});

export const syncFirestoreUsersToPouch = () => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const response = await axios.get(API_FIRESTORE_USERS);
        const users = response.data.users || [];
        let syncedCount = 0;

        for (const user of users) {
            const doc = { _id: user.id, ...user, syncedAt: new Date().toISOString() };
            try {
                const existing = await userDb.get(doc._id);
                doc._rev = existing._rev;
            } catch (err) {
                if (err.status !== 404) continue;
            }
            await userDb.put(doc);
            syncedCount++;
        }

        dispatch(setLocalProfiles(users));
        dispatch(setMessage({
            message: `✅ ${syncedCount} utilisateurs Firestore synchronisés en local.`,
            status: 'success',
            timestamp: Date.now()
        }));
    } catch (error) {
        dispatch(setMessage({
            message: `Erreur synchronisation utilisateurs Firestore: ${error.message}`,
            status: 'danger',
            timestamp: Date.now()
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const getLocalProducts = () => async dispatch => {
    try {
        dispatch(setLoading(true));

        try {
            const result = await prodDb.allDocs({
                include_docs: true,
                limit: 5000
            });

            if (result.rows.length > 0) {
                const products = result.rows.map(row => ({
                    ...row.doc,
                    _id: row.doc._id || row.id,
                    id: row.doc.id || row.doc._id
                }));

                products.sort((a, b) => {
                    const nomA = (a.nom || a.name || '').toLowerCase();
                    const nomB = (b.nom || b.name || '').toLowerCase();
                    return nomA.localeCompare(nomB);
                });

                dispatch(setLocalProducts(products));
                return;
            }
        } catch (pouchError) {
        }

        const isConnected = await testNetworkConnectivity();

        if (isConnected) {
            try {
                try {
                    const { firestore } = await import('../../config/firebase');

                    const products = [];
                    let lastDoc = null;
                    let page = 0;
                    const maxPages = 5;

                    do {
                        let query = firestore.collection('products')
                            .orderBy('__name__')
                            .limit(1000);

                        if (lastDoc) {
                            query = query.startAfter(lastDoc);
                        }

                        const snapshot = await query.get();
                        if (snapshot.empty) break;

                        snapshot.forEach(doc => {
                            const data = doc.data();
                            products.push({
                                id: doc.id,
                                _id: doc.id,
                                ...data
                            });
                            lastDoc = doc;
                        });

                        page++;
                        if (page >= maxPages) break;
                    } while (true);

                    if (products.length > 0) {
                        await prodDb.bulkDocs(products.map(p => ({
                            _id: p.id ? p.id.toString() : p._id,
                            ...p,
                            updatedAt: new Date().toISOString()
                        })), { new_edits: false });
                    }

                    dispatch(setLocalProducts(products));
                    return;
                } catch (firestoreError) {
                }

                const data = await apiCall(`${API_BASE_URL}/products?limit=5000&all=true&include=substances,interactions,posologies,equivalences,laboratoires`);
                const products = data.products || [];

                if (products.length > 0) {
                    await prodDb.bulkDocs(products.map(p => ({
                        _id: p.id ? p.id.toString() : p._id,
                        ...p,
                        updatedAt: new Date().toISOString()
                    })), { new_edits: false });
                }

                dispatch(setLocalProducts(products));
                return;
            } catch (apiError) {
            }
        }

        const offlineProducts = await getAllProductsOffline();
        dispatch(setLocalProducts(offlineProducts));

    } catch (error) {
        dispatch(setMessage({
            message: "Impossible de charger les produits.",
            status: 'danger',
            timestamp: new Date().getTime()
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const getLocalSubstances = () => async dispatch => {
    try {
        dispatch(setLoading(true));

        const result = await fetchWithOfflineSupport(
            async () => {
                try {
                    const { firestore } = await import('../../config/firebase');

                    const substances = [];
                    let lastDoc = null;
                    let page = 0;
                    const maxPages = 3;

                    do {
                        let query = firestore.collection('substances')
                            .orderBy('__name__')
                            .limit(1000);

                        if (lastDoc) {
                            query = query.startAfter(lastDoc);
                        }

                        const snapshot = await query.get();

                        if (snapshot.empty) break;

                        snapshot.forEach(doc => {
                            const data = doc.data();
                            substances.push({
                                id: doc.id,
                                _id: doc.id,
                                ...data
                            });
                            lastDoc = doc;
                        });

                        page++;
                        if (page >= maxPages) break;

                    } while (true);

                    return substances;
                } catch (firestoreError) {
                    const data = await apiCall(`${API_BASE_URL}/substances?limit=5000&all=true`);
                    return data.substances || [];
                }
            },
            async () => getAllSubstancesOffline()
        );

        const substances = result.data || [];
        dispatch(setLocalSubstances(substances));

    } catch (error) {
        dispatch(setMessage({
            message: "Impossible de charger les substances locales.",
            status: 'danger',
            timestamp: new Date().getTime()
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const getLocalProfiles = () => async dispatch => {
    try {
        dispatch(setLoading(true));

        const result = await fetchWithOfflineSupport(
            async () => {
                try {
                    const { firestore } = await import('../../config/firebase');

                    const profiles = [];
                    let lastDoc = null;
                    let page = 0;
                    const maxPages = 2;

                    do {
                        let query = firestore.collection('users')
                            .orderBy('__name__')
                            .limit(1000);

                        if (lastDoc) {
                            query = query.startAfter(lastDoc);
                        }

                        const snapshot = await query.get();

                        if (snapshot.empty) break;

                        snapshot.forEach(doc => {
                            const data = doc.data();
                            profiles.push({
                                id: doc.id,
                                _id: doc.id,
                                ...data
                            });
                            lastDoc = doc;
                        });

                        page++;
                        if (page >= maxPages) break;

                    } while (true);

                    return profiles;
                } catch (firestoreError) {
                    const data = await apiCall(`${API_BASE_URL}/allProfiles`);
                    return data.profiles || [];
                }
            },
            async () => getAllProfilesOffline()
        );

        const profiles = result.data || [];
        dispatch(setLocalProfiles(profiles));

        dispatch(setMessage({
            message: `${profiles.length} profils chargés ${result.source === 'online' ? 'depuis Firestore' : 'depuis le cache local'}.`,
            status: result.source === 'online' ? 'success' : 'info',
            timestamp: new Date().getTime()
        }));
    } catch (error) {
        dispatch(setMessage({
            message: "Impossible de charger les profils locaux.",
            status: 'danger',
            timestamp: new Date().getTime()
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const loadSubstances = (refresh = false) => async dispatch => {
    try {
        const info = await subDb.info();
        if (info.doc_count < 10 || refresh) {
            await dispatch(getLocalSubstances());
        } else {
            const allSubstances = await subDb.allDocs({ include_docs: true });
            dispatch(setLocalSubstances(allSubstances.rows.map(row => row.doc)));
        }
    } catch (err) {
        dispatch(setMessage({
            message: "Erreur lors de la récupération des substances.",
            status: 'danger',
            timestamp: new Date().getTime()
        }));
    }
};

export const deleteProduct = (productId) => async dispatch => {
    // Demander confirmation avant la suppression
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce médicament ? Cette action est irréversible.')) {
        return;
    }

    dispatch(setLoading(true));

    try {
        // Supprimer de la base de données locale
        try {
            const doc = await prodDb.get(productId);
            await prodDb.remove(doc);
        } catch (error) {
            // Si le document n'existe pas en local, on continue
            if (error.status !== 404) {
                throw error;
            }
        }

        dispatch(setMessage({
            message: 'Produit supprimé avec succès',
            status: 'success'
        }));

        // Rafraîchir la liste
        dispatch(getLocalProducts());

    } catch (error) {

        dispatch(setMessage({
            message: 'Erreur lors de la suppression du produit',
            status: 'danger'
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const syncProducts = () => async dispatch => {
    dispatch({ type: 'SYNC_PRODUCTS', payload: true });

    try {
        dispatch(setLoading(true));

        const result = await prodDb.allDocs({
            include_docs: true,
            limit: 5000
        });

        const products = result.rows.map(row => ({
            ...row.doc,
            _id: row.doc._id || row.id,
            id: row.doc.id || row.doc._id
        }));

        products.sort((a, b) => {
            const nomA = (a.nom || a.name || '').toLowerCase();
            const nomB = (b.nom || b.name || '').toLowerCase();
            return nomA.localeCompare(nomB);
        });

        dispatch(setLocalProducts(products));

        dispatch({ type: 'SYNC_PRODUCTS', payload: false });
        dispatch(setMessage({
            message: `✅ ${products.length} produits synchronisés depuis PouchDB.`,
            status: 'success',
            timestamp: new Date().getTime()
        }));

    } catch (error) {
        dispatch({ type: 'SYNC_PRODUCTS', payload: false });
        dispatch(setMessage({
            message: `❌ Erreur de synchronisation PouchDB: ${error.message}`,
            status: 'danger',
            timestamp: new Date().getTime()
        }));

    } finally {
        dispatch(setLoading(false));
    }
};

export const forceSyncFromPouchDB = () => async dispatch => {
    try {
        dispatch(setLoading(true));
        dispatch({ type: 'SYNC_PRODUCTS', payload: true });

        const result = await prodDb.allDocs({
            include_docs: true,
            attachments: false,
            binary: false
        });

        if (result.rows.length === 0) {
            dispatch(setMessage({
                message: '⚠️ Aucun produit trouvé dans PouchDB. Utilisez le téléchargement en ligne d\'abord.',
                status: 'warning',
                timestamp: new Date().getTime()
            }));
            return;
        }

        const products = result.rows.map(row => {
            const doc = row.doc;
            return {
                ...doc,
                _id: doc._id || row.id,
                id: doc.id || doc._id,
                _rev: undefined
            };
        }).filter(product =>
            product && (product.nom || product.name)
        );

        products.sort((a, b) => {
            const nomA = (a.nom || a.name || '').toLowerCase();
            const nomB = (b.nom || b.name || '').toLowerCase();
            return nomA.localeCompare(nomB);
        });

        dispatch(setLocalProducts(products));

        dispatch(setMessage({
            message: `✅ ${products.length} produits synchronisés depuis PouchDB local.`,
            status: 'success',
            timestamp: new Date().getTime()
        }));

    } catch (error) {
        dispatch(setMessage({
            message: `❌ Erreur synchronisation PouchDB: ${error.message}`,
            status: 'danger',
            timestamp: new Date().getTime()
        }));
    } finally {
        dispatch(setLoading(false));
        dispatch({ type: 'SYNC_PRODUCTS', payload: false });
    }
};

export const preloadProductsByIds = (productIDs) => async dispatch => {
    if (!productIDs || productIDs.length === 0) {
        dispatch(setMessage({
            message: 'Aucun produit à précharger.',
            status: 'info',
            timestamp: new Date().getTime()
        }));
        return;
    }

    try {
        dispatch(setLoading(true));

        const productBatches = Math.ceil(productIDs.length / CHUNK_SIZE);
        let totalProcessed = 0;

        for (let i = 0; i < productBatches; i++) {
            if (i > 0) await wait(REQUEST_DELAY);

            const batch = productIDs.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);

            try {
                const data = await apiCall(`${API_BASE_URL}/productsForIds`, {
                    method: "POST",
                    body: JSON.stringify({ ids: batch })
                });

                if (data.products && data.products.length > 0) {
                    const formattedProducts = data.products.map(p => ({
                        _id: p.id ? p.id.toString() : p._id,
                        name: p.name,
                        price: p.price,
                        categoryId: p.categoryId,
                        ...p,
                        updatedAt: new Date().toISOString(),
                    }));

                    await prodDb.bulkDocs(formattedProducts, { new_edits: false });
                    totalProcessed += formattedProducts.length;
                }
            } catch (batchError) {
            }
        }

        const allProducts = await prodDb.allDocs({ include_docs: true });
        dispatch(setLocalProducts(allProducts.rows.map(row => row.doc)));

        dispatch(setMessage({
            message: `Préchargement terminé: ${totalProcessed} produits disponibles hors ligne.`,
            status: 'success',
            timestamp: new Date().getTime()
        }));
    } catch (error) {
        dispatch(setMessage({
            message: `Erreur de préchargement: ${error.message}`,
            status: 'danger',
            timestamp: new Date().getTime()
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const preloadSubstancesByIds = (substanceIDs) => async dispatch => {
    if (!substanceIDs || substanceIDs.length === 0) {
        dispatch(setMessage({
            message: 'Aucune substance à précharger.',
            status: 'info',
            timestamp: new Date().getTime()
        }));
        return;
    }

    try {
        dispatch(setLoading(true));

        const substanceChunks = [];
        for (let i = 0; i < substanceIDs.length; i += CHUNK_SIZE) {
            substanceChunks.push(substanceIDs.slice(i, i + CHUNK_SIZE));
        }

        let totalProcessed = 0;

        for (const chunk of substanceChunks) {
            if (totalProcessed > 0) await wait(REQUEST_DELAY);

            try {
                const data = await apiCall(`${API_BASE_URL}/substancesForIds`, {
                    method: 'POST',
                    body: JSON.stringify({ ids: chunk })
                });

                if (data.substances && data.substances.length > 0) {
                    const formattedSubstances = data.substances.map(s => ({
                        _id: s.id ? s.id.toString() : s._id,
                        ...s,
                        updatedAt: new Date().toISOString()
                    }));

                    await subDb.bulkDocs(formattedSubstances, { new_edits: false });
                    totalProcessed += formattedSubstances.length;
                }
            } catch (chunkError) {
            }
        }

        const allSubstances = await subDb.allDocs({ include_docs: true });
        dispatch(setLocalSubstances(allSubstances.rows.map(row => row.doc)));

        dispatch(setMessage({
            message: `Préchargement terminé: ${totalProcessed} substances disponibles hors ligne.`,
            status: 'success',
            timestamp: new Date().getTime()
        }));
    } catch (error) {
        dispatch(setMessage({
            message: `Erreur de préchargement: ${error.message}`,
            status: 'danger',
            timestamp: new Date().getTime()
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const preloadProfilesByIds = (profileIDs) => async dispatch => {
    if (!profileIDs || profileIDs.length === 0) {
        dispatch(setMessage({
            message: 'Aucun profil à précharger.',
            status: 'info',
            timestamp: new Date().getTime()
        }));
        return;
    }

    try {
        dispatch(setLoading(true));

        const profileChunks = [];
        for (let i = 0; i < profileIDs.length; i += CHUNK_SIZE) {
            profileChunks.push(profileIDs.slice(i, i + CHUNK_SIZE));
        }

        let totalProcessed = 0;

        for (const chunk of profileChunks) {
            if (totalProcessed > 0) await wait(REQUEST_DELAY);

            try {
                const data = await apiCall(`${API_BASE_URL}/profilesForIds`, {
                    method: 'POST',
                    body: JSON.stringify({ ids: chunk })
                });

                if (data.profiles && data.profiles.length > 0) {
                    const formattedProfiles = data.profiles.map(p => ({
                        _id: p._id || p.id,
                        ...p,
                        updatedAt: new Date().toISOString()
                    }));

                    await userDb.bulkDocs(formattedProfiles, { new_edits: false });
                    totalProcessed += formattedProfiles.length;
                }
            } catch (chunkError) {
            }
        }

        const allProfiles = await userDb.allDocs({ include_docs: true });
        dispatch(setLocalProfiles(allProfiles.rows.map(row => row.doc)));

        dispatch(setMessage({
            message: `Préchargement terminé: ${totalProcessed} profils disponibles hors ligne.`,
            status: 'success',
            timestamp: new Date().getTime()
        }));
    } catch (error) {
        dispatch(setMessage({
            message: `Erreur de préchargement: ${error.message}`,
            status: 'danger',
            timestamp: new Date().getTime()
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const initializeOfflineData = (profileIDs = []) => async dispatch => {
    try {
        dispatch(setLoading(true));

        await Promise.all([
            dispatch(getLocalProducts()),
            dispatch(getLocalSubstances()),
            profileIDs.length > 0
                ? dispatch(preloadProfilesByIds(profileIDs))
                : dispatch(getLocalProfiles())
        ]);

        dispatch(setMessage({
            message: `Données hors ligne initialisées avec succès.`,
            status: 'success',
            timestamp: new Date().getTime()
        }));
    } catch (error) {
        dispatch(setMessage({
            message: `Erreur lors de l'initialisation des données hors ligne: ${error.message}`,
            status: 'danger',
            timestamp: new Date().getTime()
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

const findUserByEmail = async (email) => {
    try {
        const result = await userDb.allDocs({ include_docs: true });
        const users = result.rows.map(row => row.doc);
        const user = users.find(u => u.email && u.email.toLowerCase() === email.toLowerCase());
        return user || null;
    } catch (err) {
        return null;
    }
};

export const authenticateOffline = (email, password) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setAuthenticating(true));

        if (!email) throw new Error("Email requis");

        // Vérifier la connectivité réseau
        const hasNetwork = await testNetworkConnectivity(3000);

        if (!hasNetwork) {
            dispatch(setMessage({
                message: '📶 Mode hors ligne activé automatiquement - pas de connexion internet',
                status: 'info',
                timestamp: Date.now(),
            }));
        }

        const userDoc = await findUserByEmail(email);

        if (!userDoc) {
            if (!hasNetwork) {
                throw new Error("Utilisateur non trouvé dans les données hors ligne. Une connexion internet est nécessaire pour la première connexion.");
            } else {
                throw new Error("Utilisateur non trouvé dans les données hors ligne");
            }
        }

        // Vérification du mot de passe hors ligne.
        // - Si un hash est enregistré : le mot de passe est obligatoire et vérifié.
        // - Sinon (compte synchronisé avant cette fonctionnalité) : connexion par
        //   email seul, jusqu'à la prochaine connexion en ligne qui enregistrera le hash.
        const { verifyPassword, isHashedPassword } = await import('../../helpers/passwordHash');
        if (isHashedPassword(userDoc.passwordHash)) {
            if (!password) {
                throw new Error("Mot de passe requis pour la connexion hors ligne.");
            }
            const valid = await verifyPassword(password, userDoc.passwordHash);
            if (!valid) {
                throw new Error("Mot de passe incorrect.");
            }
        }

        // Mettre à jour la dernière utilisation
        try {
            const { updateUserLastSync } = await import('../../helpers/offlineUserManager');
            await updateUserLastSync(email);
        } catch (syncError) {
            // Ignore sync error
        }

        dispatch({
            type: SYNC_OFFLINE_USER,
            payload: {
                _id: userDoc._id,
                email: userDoc.email,
                name: userDoc.name,
                profile: userDoc.profile || {},
                uid: userDoc.uid || userDoc._id,
                displayName: userDoc.displayName || userDoc.name,
                lastLoginOffline: new Date().toISOString(),
                isOfflineMode: !hasNetwork
            },
        });

        dispatch({
            type: 'SET_OFFLINE_AUTH_DONE',
            payload: true,
        });

        localStorage.setItem('lastOfflineUser', JSON.stringify({
            email: userDoc.email,
            timestamp: Date.now(),
        }));

        dispatch(setMessage({
            message: `✅ Connexion hors ligne réussie pour ${userDoc.email}`,
            status: 'success',
            timestamp: Date.now(),
        }));

        return userDoc;

    } catch (error) {
        dispatch({
            type: 'SET_OFFLINE_AUTH_DONE',
            payload: false,
        });

        // Message d'erreur plus spécifique selon le type d'erreur
        let errorMessage = error.message;

        if (error.message.includes('non trouvé dans les données hors ligne')) {
            errorMessage += ' Connectez-vous d\'abord en ligne pour synchroniser vos données.';
        }

        dispatch(setMessage({
            message: errorMessage,
            status: 'danger',
            timestamp: Date.now(),
        }));
        throw error;
    } finally {
        dispatch(setAuthenticating(false));
        dispatch(setLoading(false));
    }
};

export const loadDataFromLocalJson = () => async dispatch => {
    try {
        const [
            professionalsModule,
            classesModule,
            formesModule,
            dosagesModule,
            profilesModule
        ] = await Promise.all([
            import('../../json/professionals.json'),
            import('../../json/classe.json'),
            import('../../json/forme.json'),
            import('../../json/dosage.json'),
            import('../../json/profile.json')
        ]);

        const professionals = professionalsModule.default || [];
        const classes = classesModule.default || [];
        const formes = formesModule.default || [];
        const dosages = dosagesModule.default || [];
        const profiles = profilesModule.default || [];

        const syntheticProducts = [];
        for (let i = 0; i < Math.min(classes.length, 1000); i++) {
            const classe = classes[i];
            const forme = formes[i % formes.length];
            const dosage = dosages[i % dosages.length];

            syntheticProducts.push({
                id: `synthetic_${i}`,
                _id: `synthetic_${i}`,
                nom: `${classe.nom_classe || classe.name || 'Médicament'} ${forme.nom_forme || forme.name || ''}`,
                DCI: classe.nom_classe || classe.name || 'DCI',
                familleTherapeutique: classe.nom_classe || classe.name || '',
                classesPharmacologiques: classe.nom_classe || classe.name || '',
                forme: forme.nom_forme || forme.name || 'Comprimé',
                dosage: dosage.nom_dosage || dosage.name || '100mg',
                conditionnement: 'Boîte de 30',
                prix: '1500 DA',
                laboratoire: 'Laboratoire Local',
                source: 'json_local'
            });
        }

        const syntheticSubstances = classes.slice(0, 2000).map((classe, index) => ({
            id: `substance_${index}`,
            _id: `substance_${index}`,
            name: classe.nom_classe || classe.name || 'Substance',
            nom: classe.nom_classe || classe.name || 'Substance',
            DCI: classe.nom_classe || classe.name || 'DCI',
            source: 'json_local'
        }));

        dispatch(setLocalProducts(syntheticProducts));
        dispatch(setLocalSubstances(syntheticSubstances));
        dispatch(setLocalProfiles(profiles));

        const { setAutocompleteProducts } = await import('./product');
        const autocompleteData = syntheticProducts.slice(0, 100).map(p => ({
            nom: p.nom,
            DCI: p.DCI,
            _id: p._id
        }));
        dispatch(setAutocompleteProducts(autocompleteData));

        dispatch(setMessage({
            message: `📂 ${syntheticProducts.length} produits, ${syntheticSubstances.length} substances et ${profiles.length} profils chargés depuis fichiers JSON locaux.`,
            status: 'success',
            timestamp: new Date().getTime()
        }));

        return true;
    } catch (error) {
        dispatch(setMessage({
            message: 'Erreur lors du chargement des fichiers JSON locaux.',
            status: 'warning',
            timestamp: new Date().getTime()
        }));
        return false;
    }
};

export const loadAllData = () => async (dispatch, getState) => {
    try {
        dispatch(setLoading(true));

        const online = await testNetworkConnectivity();

        if (!online) {
            const offlineProducts = await getAllProductsOffline();
            const offlineSubstances = await getAllSubstancesOffline();
            const offlineProfiles = [];

            dispatch(setLocalProducts(offlineProducts));
            dispatch(setLocalSubstances(offlineSubstances));
            dispatch(setLocalProfiles(offlineProfiles));

            return;
        }

        const [productsResult, substancesResult, profilesResult] = await Promise.allSettled([
            dispatch(getLocalProducts()),
            dispatch(getLocalSubstances()),
            dispatch(getLocalProfiles())
        ]);

        let totalLoaded = 0;
        let loadedTypes = [];

        if (productsResult.status === 'fulfilled') {
            loadedTypes.push('Produits');
            totalLoaded++;
        }

        if (substancesResult.status === 'fulfilled') {
            loadedTypes.push('Substances');
            totalLoaded++;
        }

        if (profilesResult.status === 'fulfilled') {
            loadedTypes.push('Profils');
            totalLoaded++;
        }

        setTimeout(async () => {
            try {
                const { setAutocompleteProducts } = await import('./product');
                const state = getState();
                const products = state?.products?.products || [];
                if (products.length > 0) {
                    const autocompleteData = products.slice(0, 100).map(p => ({
                        nom: p.nom || p.name || 'Produit',
                        DCI: p.DCI || p.dci || '',
                        _id: p.id || p._id
                    }));
                    dispatch(setAutocompleteProducts(autocompleteData));
                }
            } catch (autocompleteError) {
            }
        }, 2000);

        dispatch(setMessage({
            message: `✅ ${totalLoaded}/3 types de données chargés: ${loadedTypes.join(', ')}`,
            status: totalLoaded >= 2 ? 'success' : 'warning',
            timestamp: Date.now()
        }));

    } catch (error) {

        try {
            const offlineProducts = await getAllProductsOffline();
            const offlineSubstances = await getAllSubstancesOffline();
            const offlineProfiles = await getAllProfilesOffline();

            dispatch(setLocalProducts(offlineProducts));
            dispatch(setLocalSubstances(offlineSubstances));
            dispatch(setLocalProfiles(offlineProfiles));

            dispatch(setMessage({
                message: "Mode hors ligne activé - Données locales chargées",
                status: 'info',
                timestamp: Date.now()
            }));
        } catch (offlineError) {
            dispatch(setMessage({
                message: "Impossible de charger les données (en ligne et hors ligne)",
                status: 'danger',
                timestamp: Date.now()
            }));
        }
    } finally {
        dispatch(setLoading(false));
    }
};

export default {
    setLocalProducts,
    setLocalSubstances,
    setLocalCategories,
    setLocalProfiles,
    getLocalProducts,
    getLocalSubstances,
    getLocalProfiles,
    loadSubstances,
    deleteProduct,
    syncProducts,
    forceSyncFromPouchDB,
    preloadProductsByIds,
    preloadSubstancesByIds,
    preloadProfilesByIds,
    initializeOfflineData,
    authenticateOffline,
    loadDataFromLocalJson,
    loadAllData
};

